import {
  Grid,
  Stack,
  Tooltip,
  TooltipProps,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import { formatDate, formatDistanceToNow } from "date-fns";
import Markdown from "react-markdown";

import { Label } from "../../../Label/Label";
import { ModelCardFragment } from "../../models.graphql";
import { ModelCapabilities } from "../ModelCapabilities/ModelCapabilities";
import { ModelBaseModelIndicator } from "./ModelBaseModelIndicator";
import { ModelDurationIndicator } from "./ModelDurationIndicator";

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.secondary[5],
    border: `1px solid ${theme.palette.secondary[6]}`,
    boxShadow: "0px 16px 70px rgba(0, 0, 0, 0.50)",
    color: theme.palette.secondary[2],
    maxWidth: 620,
  },
}));

const WorkspaceLogo = styled("img")(() => ({
  height: 32,
  width: 32,
  borderRadius: 4,
}));

interface ModelPreviewTooltipProps
  extends Omit<TooltipProps, "title" | "model"> {
  model: ModelCardFragment;
  isUserWorkspaceModel: boolean;
  onClickLabel?: (event: React.MouseEvent, labelId: string) => void;
}

export const ModelPreviewTooltip = (props: ModelPreviewTooltipProps) => {
  const { model, isUserWorkspaceModel, onClickLabel, ...rest } = props;

  return (
    <HtmlTooltip
      enterDelay={800}
      enterNextDelay={800}
      placement="right"
      title={
        <ModelPreviewTooltipInner
          model={model}
          isUserWorkspaceModel={isUserWorkspaceModel}
          onClickLabel={onClickLabel}
        />
      }
      {...rest}
    />
  );
};

interface ModelPreviewTooltipInnerProps {
  model: ModelCardFragment;
  isUserWorkspaceModel: boolean;
  onClickLabel?: (event: React.MouseEvent, labelId: string) => void;
}

const ModelPreviewTooltipInner = (props: ModelPreviewTooltipInnerProps) => {
  const { model, isUserWorkspaceModel, onClickLabel } = props;

  const theme = useTheme();

  let creator = `${model.workspace?.displayName || "Unknown"}`;
  if (isUserWorkspaceModel) {
    creator = `${model.createdBy?.name || "someone"} at ${creator}`;
  }

  const creationDate = formatDate(new Date(model.createdAt), "MM-dd-yy");
  const updateDate = formatDistanceToNow(
    new Date(model.updatedAt ?? model.createdAt),
    {
      addSuffix: true,
    },
  );
  const caption = `Created ${creationDate} · Updated ${updateDate}`;

  return (
    <Grid container spacing={1}>
      <Grid size={6}>
        <Stack
          direction="column"
          gap={2}
          sx={{ flex: 1, padding: theme.spacing(4) }}
        >
          <Typography sx={{ "& a": { color: theme.palette.primary.light } }}>
            <Markdown>
              {model.description ||
                "This model has no description. Try it to see its magic!"}
            </Markdown>
          </Typography>
          <Stack
            direction="row"
            sx={{ columnGap: 1, flexWrap: "wrap", rowGap: 1 }}
          >
            {model.labels.map((label) => (
              <Label
                key={label.id}
                id={String(label.id)}
                label={label.name}
                onClick={onClickLabel}
              />
            ))}
          </Stack>
          <Typography variant="caption">{caption}</Typography>
          {model.deprecatedAt && (
            <Stack direction="row" gap={1}>
              <Typography variant="caption" color="warning.main">
                Leaving{" "}
                {formatDate(new Date(model.deprecatedAt), "MMM d, yyyy")}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Grid>
      <Grid size={6} sx={{ display: "flex" }}>
        <Stack
          direction="column"
          gap={1}
          sx={{ flex: 1, padding: theme.spacing(2) }}
        >
          <Stack
            direction="row"
            gap={2}
            sx={{
              alignItems: "center",
              backgroundColor: theme.palette.secondary["4/50"],
              borderTopLeftRadius: theme.spacing(2),
              borderTopRightRadius: theme.spacing(2),
              padding: theme.spacing(4),
            }}
          >
            {model.workspace?.profilePicture && (
              <WorkspaceLogo
                alt={model.workspace?.displayName}
                src={model.workspace?.profilePicture}
              />
            )}
            <Stack direction="column" sx={{ alignItems: "flex-start" }}>
              <Typography sx={{ fontWeight: 500 }}>{model.name}</Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 300, color: theme.palette.secondary[3] }}
              >
                by {creator}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction="column"
            spacing={4}
            sx={{
              backgroundColor: theme.palette.secondary["4/50"],
              borderBottomLeftRadius: theme.spacing(2),
              borderBottomRightRadius: theme.spacing(2),
              flex: 1,
              padding: theme.spacing(4),
            }}
          >
            <Stack direction="row" gap={2}>
              <ModelBaseModelIndicator model={model} />
              <ModelDurationIndicator model={model} />
            </Stack>
            <ModelCapabilities
              modality={model.modality}
              capabilities={model.capabilities ?? undefined}
            />
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};
